/**
 * Front door - collects the DNN API key, validates it, and stores it in a cookie.
 * The key is never rendered/logged anywhere - only kept in local input state until
 * validateApiKey() confirms it, at which point it's written to the cookie via
 * setApiKeyCookie() and this component unmounts.
 */

import { useState } from 'react';
import { validateApiKey } from '../api/client';
import { setApiKeyCookie } from '../utils/authCookie';
import { LoadingScreen } from './LoadingScreen';
import './LoginGate.css';

interface LoginGateProps {
  onSuccess: () => void;
}

export function LoginGate({ onSuccess }: LoginGateProps) {
  const [apiKey, setApiKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey || isValidating) return;

    setIsValidating(true);
    setError(null);
    try {
      const isValid = await validateApiKey(apiKey);
      if (isValid) {
        setApiKeyCookie(apiKey);
        onSuccess();
      } else {
        setError('Invalid API key');
      }
    } catch {
      setError('Could not reach the server - please try again');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="login-gate">
      <form className="login-gate-card card" onSubmit={handleSubmit}>
        <h1 className="login-gate-title">RAIQON</h1>
        <p className="login-gate-subtitle">Enter your API key to continue</p>

        <div className="config-field">
          <label className="config-label" htmlFor="login-gate-api-key">
            API Key
          </label>
          <input
            id="login-gate-api-key"
            className="config-input"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={isValidating}
            autoFocus
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          className="button-primary login-gate-submit"
          disabled={isValidating || !apiKey}
        >
          {isValidating ? <LoadingScreen variant="compact" /> : 'Continue'}
        </button>
      </form>
    </div>
  );
}
